import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ADMIN_APPTS_QUERY_KEY,
  getAppointmentAuditLog,
  listAppointmentsAdmin,
} from "@/api/appointmentApi";
import type { AdminAppointmentFilters } from "@/api/appointmentApi";
import { PageHeader } from "@/components/common/PageHeader";
import type { Appointment, AppointmentAuditLog, AppointmentStatus } from "@/types/domain";
import HistoryIcon from "@mui/icons-material/History";

const statusColors: Record<string, "default" | "warning" | "success" | "error" | "info" | "primary"> = {
  PENDING: "warning",
  CONFIRMED: "info",
  COMPLETED: "success",
  CANCELLED: "default",
  CANCELLED_BY_PATIENT: "default",
  CANCELLED_BY_DOCTOR: "default",
  CANCELLED_BY_ADMIN: "default",
  NO_SHOW_PATIENT: "error",
  NO_SHOW_DOCTOR: "error",
  RESCHEDULED: "primary",
};

const ALL_STATUSES: AppointmentStatus[] = [
  "PENDING", "CONFIRMED", "COMPLETED",
  "CANCELLED_BY_PATIENT", "CANCELLED_BY_DOCTOR", "CANCELLED_BY_ADMIN",
  "NO_SHOW_PATIENT", "NO_SHOW_DOCTOR", "RESCHEDULED",
];

function formatStatus(s: string) {
  return s.replace(/_/g, " ");
}

export default function AdminAppointmentsPage() {
  const [filters, setFilters] = useState<AdminAppointmentFilters>({
    page: 0,
    size: 20,
  });
  const [auditAppt, setAuditAppt] = useState<Appointment | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: [...ADMIN_APPTS_QUERY_KEY, filters],
    queryFn: () => listAppointmentsAdmin(filters),
  });

  const auditQuery = useQuery({
    queryKey: ["appointment-audit-log", auditAppt?.id],
    queryFn: () => getAppointmentAuditLog(auditAppt!.id),
    enabled: !!auditAppt,
  });

  const updateFilter = (key: keyof AdminAppointmentFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined, page: 0 }));
  };

  return (
    <Box>
      <PageHeader
        title="All Appointments"
        subtitle="View and manage all appointments across the platform."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load appointments.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" flexWrap="wrap" gap={2}>
          <TextField
            label="Doctor ID"
            size="small"
            value={filters.doctorId ?? ""}
            onChange={(e) => updateFilter("doctorId", e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Patient ID"
            size="small"
            value={filters.patientId ?? ""}
            onChange={(e) => updateFilter("patientId", e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Status"
            size="small"
            select
            value={filters.status ?? ""}
            onChange={(e) => updateFilter("status", e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            {ALL_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{formatStatus(s)}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Booked from"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.bookedDateFrom ?? ""}
            onChange={(e) => updateFilter("bookedDateFrom", e.target.value)}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="Booked to"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.bookedDateTo ?? ""}
            onChange={(e) => updateFilter("bookedDateTo", e.target.value)}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="Appt from"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.apptDateFrom ?? ""}
            onChange={(e) => updateFilter("apptDateFrom", e.target.value)}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="Appt to"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.apptDateTo ?? ""}
            onChange={(e) => updateFilter("apptDateTo", e.target.value)}
            sx={{ minWidth: 160 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => setFilters({ page: 0, size: 20 })}
          >
            Reset
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Booked</TableCell>
              <TableCell>Appointment Date</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Doctor</TableCell>
              <TableCell align="right">Fee</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Lifecycle</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Box sx={{ display: "grid", placeItems: "center", py: 3 }}>
                    <CircularProgress size={20} />
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (!data || data.content.length === 0) && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" py={2}>
                    No appointments match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {data?.content.map((a) => (
              <TableRow key={a.id}>
                <TableCell>
                  <Typography variant="body2">
                    {a.createdAt ? format(new Date(a.createdAt), "d MMM yyyy") : "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {a.createdAt ? format(new Date(a.createdAt), "h:mm a") : ""}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {format(new Date(a.slotStart), "d MMM yyyy")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(a.slotStart), "h:mm a")} – {format(new Date(a.slotEnd), "h:mm a")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title={a.patientId}>
                    <Typography variant="body2" fontFamily="monospace">
                      {a.patientName ?? a.patientId.slice(0, 8) + "…"}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={a.doctorId}>
                    <Typography variant="body2" fontFamily="monospace">
                      {a.doctorName ?? a.doctorId.slice(0, 8) + "…"}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={700}>
                    {a.consultationFee != null ? `₹${a.consultationFee}` : "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatStatus(a.status)}
                    color={statusColors[a.status] ?? "default"}
                    size="small"
                    sx={{ fontWeight: 700 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View lifecycle">
                    <IconButton size="small" onClick={() => setAuditAppt(a)}>
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data && (
          <TablePagination
            component="div"
            count={data.totalElements}
            page={data.number}
            rowsPerPage={data.size}
            onPageChange={(_, page) => setFilters((f) => ({ ...f, page }))}
            onRowsPerPageChange={(e) =>
              setFilters((f) => ({ ...f, size: parseInt(e.target.value, 10), page: 0 }))
            }
            rowsPerPageOptions={[10, 20, 50]}
          />
        )}
      </Paper>

      <Dialog
        open={!!auditAppt}
        onClose={() => setAuditAppt(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Appointment Lifecycle
          {auditAppt && (
            <Typography variant="body2" color="text.secondary" fontFamily="monospace">
              {auditAppt.id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {auditQuery.isLoading && (
            <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {auditQuery.data && auditQuery.data.length === 0 && (
            <Typography color="text.secondary">No audit entries found.</Typography>
          )}
          {auditQuery.data && auditQuery.data.length > 0 && (
            <AuditTimeline entries={auditQuery.data} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function AuditTimeline({ entries }: { entries: AppointmentAuditLog[] }) {
  return (
    <Box sx={{ pl: 0 }}>
      {entries.map((e, idx) => (
        <Box key={e.id} sx={{ display: "flex", gap: 2, mb: idx < entries.length - 1 ? 2 : 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: "primary.main",
                mt: 0.5,
              }}
            />
            {idx < entries.length - 1 && (
              <Box sx={{ width: 2, flex: 1, bgcolor: "divider" }} />
            )}
          </Box>
          <Box sx={{ pb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {formatStatus(e.eventType)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {format(new Date(e.createdAt), "d MMM yyyy, h:mm:ss a")}
              {" · "}
              {e.actorRole === "SYSTEM" ? "System" : `${e.actorRole} (${e.actorId.slice(0, 8)}…)`}
            </Typography>
            {e.details && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontFamily: "monospace", fontSize: 12 }}>
                {e.details}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
